import Axios, { AxiosRequestConfig } from 'axios';
import { Endpoints, ErrorResponse } from '@/types/endpoints';

const API_ORIGIN = process.env.NEXT_PUBLIC_APP_API_HOST ?? '';

/**
 * Sanctum SPA 認証の CSRF Cookie (XSRF-TOKEN) を取得する。
 * ログインや状態変更リクエストの前に一度呼んでおくと、axios が
 * 以降の `X-XSRF-TOKEN` ヘッダを自動付与できる。
 */
export async function ensureCsrf(): Promise<void> {
  await Axios.get(`${API_ORIGIN}/sanctum/csrf-cookie`, {
    withCredentials: true,
  });
}

// 未ログイン(401)でもアクセスを許可するゲストページ。
// これらの画面では session-user の 401 でログイン画面へ転送しない
// (パスワード再設定リンクからの遷移などを弾かないため)。
const GUEST_PATHS = ['/login', '/forgot-password', '/reset-password'];

function redirectToLoginIfNeeded(): void {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (GUEST_PATHS.includes(path)) return;
  if (path.startsWith('/auth/')) return;
  window.location.href = '/login';
}

export const axios = Axios.create({
  baseURL: API_ORIGIN + '/api',
  responseType: 'json',
  timeout: 10000,
  // Cookie セッション認証 (SPA) のためクレデンシャルと CSRF トークン送出を有効化。
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// session-user は「現在のログイントークンが有効か」を判定する役割も兼ねているため、
// 401 だけでなく 500 等の予期しないエラーが返った場合もトークン切れとみなして
// ログイン画面へ誘導する（DBリセットでトークンが orphan 化したケースなどを救済）。
function isSessionCheckUrl(url?: string): boolean {
  if (!url) return false;
  return url.includes('/v1/admin/session-user');
}

function handleAuthFailure(): void {
  if (typeof window === 'undefined') return;
  redirectToLoginIfNeeded();
}

axios.interceptors.response.use(
  (response) => {
    const { data, config } = response;
    if ('error' in data) {
      const code = data.error?.code;
      if (code === 401) {
        handleAuthFailure();
      } else if (code >= 500 && isSessionCheckUrl(config?.url)) {
        handleAuthFailure();
      }
      return data as ErrorResponse;
    }
    if (data.limit) {
      return {
        data: data.data,
        limit: data.limit,
        offset: data.offset,
        total: data.total,
      };
    }
    return data.data;
  },
  async (error) => {
    const status = error.response?.status;
    const url = error.config?.url;
    // CSRF トークン不一致 (419) は CSRF Cookie を取り直して 1 度だけ再試行する。
    const cfg = error.config as
      | (AxiosRequestConfig & { __csrfRetried?: boolean })
      | undefined;
    if (status === 419 && cfg && !cfg.__csrfRetried) {
      cfg.__csrfRetried = true;
      try {
        await ensureCsrf();
        return axios.request(cfg);
      } catch {
        // 再試行も失敗した場合は通常のエラー処理へフォールスルー
      }
    }
    if (status === 401) {
      handleAuthFailure();
    } else if (status >= 500 && isSessionCheckUrl(url)) {
      handleAuthFailure();
    }
    return Promise.reject(error);
  },
);

axios.interceptors.request.use(async (request) => {
  let params = request.params;
  if (['post', 'patch', 'put'].includes(request.method ?? '')) {
    if (request.data instanceof FormData) {
      params = {};
      for (let [key, value] of request.data.entries()) {
        params[key] = value;
      }
    } else {
      params = request.data;
    }
  }

  if (request.url && request.baseURL) {
    request.url = replaceUrl(request.url, request.baseURL, params);
  }
  return request;
});

/** :idのような文字列をparamsの値に置換する処理 */
function replaceUrl<T extends { [key: string]: any }>(
  url: string,
  baseURL: string,
  params: T,
): string {
  (url.match(/:([a-zA-Z0-9]+)/g) || []).forEach((value) => {
    const index = value.replace(/:/i, '');
    if (params[index]) {
      url = url.replace(new RegExp(value, 'i'), params[index]);
      delete params[index];
    }
    value = value.replace(/:/i, '');
  });

  return !url.match(/http/g) ? baseURL + url : url;
}

export const api: Endpoints = {
  get<T = any>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    return axios.get(endpoint, config);
  },
  post<T = any>(
    endpoint: string,
    data: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return axios.post(endpoint, data, config);
  },
  patch<T = any>(
    endpoint: string,
    data: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return axios.patch(endpoint, data, config);
  },
  put<T = any>(
    endpoint: string,
    data: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return axios.put(endpoint, data, config);
  },
  delete<T = any>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    return axios.delete(endpoint, config);
  },
};

export default api;
